/**
 * In-memory capture of the landing UTMs. Captured on landing regardless of
 * consent — nothing is written to the device, so it's PECR-clean (mirrors the
 * lead_id capture) — and read when the Braze SDK initialises, which only
 * happens after marketing consent. Whitelist-filtered, so the attribution
 * `lead_id` can never be included.
 */

// Standard UTMs + major ad-platform click-ids. lead_id is intentionally absent.
const UTM_PARAM_WHITELIST = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'gclid',
  'fbclid',
  'msclkid',
] as const

export type LandingUtms = Partial<Record<(typeof UTM_PARAM_WHITELIST)[number], string>>

// In-memory only. Cleared on full page reload; re-hydrated by the next capture
// if the params are still on the URL.
let storedUtms: LandingUtms | null = null

/** Extracts the whitelisted utm/click-id params from search params. */
export function extractUtms(params: URLSearchParams | null | undefined): LandingUtms {
  const utms: LandingUtms = {}
  if (!params) return utms
  for (const key of UTM_PARAM_WHITELIST) {
    const value = params.get(key)?.trim()
    if (value) utms[key] = value
  }
  return utms
}

/**
 * Captures the landing UTMs into memory. First-touch wins (a later navigation
 * never clobbers them). No-op without whitelisted params. Defaults to the
 * current URL so callers without the router can use it.
 */
export function captureUtms(
  params: URLSearchParams | null | undefined = typeof window === 'undefined'
    ? null
    : new URLSearchParams(window.location.search),
): void {
  // Client-only: the module global is shared across requests on the server.
  if (typeof window === 'undefined' || storedUtms) return
  const utms = extractUtms(params)
  if (Object.keys(utms).length > 0) storedUtms = utms
}

/** Reads the captured UTMs, or null if none captured this session. */
export function getStoredUtms(): LandingUtms | null {
  return storedUtms
}

/** Test-only: reset the in-memory store. */
export function resetStoredUtms(): void {
  storedUtms = null
}
